// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { cartContainsNonOrDifferentPrebookItems } from "@synd/App/Store/Selectors/CartSelector";
import { parseBoolean } from "@synd/XDL/Utilities";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Change
    isPDPActions?: boolean;
    disabled?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    // XNG-Change
    const cartState = getCurrentCartState(state);

    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        // XNG-Change
        cartHasNonPrebook: cartState.value
            ? cartContainsNonOrDifferentPrebookItems(
                  cartState.value,
                  props.productContext.product?.properties?.prebookSeason,
              )
            : false,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, cantBuy, properties, productNumber, productTitle },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, productDetailPath, inventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    // XNG-Change
    isPDPActions,
    cartHasNonPrebook,
    disabled,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const isDisabled =
        (!cantBuy && (isAddToCartButtonDisabled || (!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart)) ||
        (cantBuy && isPDPActions);
    // XNG-Change
    const [isLoading, setIsLoading] = useState(false);

    // XNG-Change
    const dispatch = useDispatch();

    // XNG-Change show disabled
    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }

    // XNG-Change
    const addToCartClickHandler = async () => {
        if (isPDPActions) {
            dipatchQtyAdjustedNotify(false);
        }
        // check if the requested qty > available qty, then display a warning modal and do not add to cart
        if (qtyOrdered > (inventory?.qtyOnHand || 0)) {
            dispatch({
                type: "xCommon/update",
                data: {
                    key: "isInsufficientInventoryModal",
                    value: {
                        isOpen: true,
                        productInfo: { productNumber, qtyOnHand: inventory?.qtyOnHand, qtyOrdered, productTitle },
                    },
                },
            } as any);
            return;
        }

        // if product is prebook, display prebook modal if cart has no-prebook
        if (parseBoolean(properties?.isPrebook) && cartHasNonPrebook) {
            dispatch({
                type: "xCommon/update",
                data: {
                    key: "isPreBookModalOpen",
                    value: true,
                },
            } as any);
            return;
        }
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        setIsLoading(true);
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            // xng-change handle error.
            onError: error => {
                toasterContext.addToast({
                    body: error,
                    messageType: "danger",
                    timeoutLength: addToCartPopupTimeout,
                });
            },
        })) as CartLineModel;
        setIsLoading(false);

        if (showAddToCartConfirmationDialog) {
            // if its pdp, we need to display the notify above the add to cart button instead of toaster
            if (isPDPActions && cartline.isQtyAdjusted) {
                dipatchQtyAdjustedNotify(true);
                return;
            }
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    const dipatchQtyAdjustedNotify = (display: boolean) => {
        dispatch({
            type: "xProductDetails/DisplayQtyAdjustedNotify",
            displayQtyAdjustedNotify: display,
        } as any);
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isDisabled || disabled}
            {...otherProps}
            {...{ isLoading }}
        >
            {labelOverride ?? translate(cantBuy && !isPDPActions ? "View Product" : "Add to Cart")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
